import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import ProductCard from '@/components/products/ProductCard';
import api from '@/lib/api';

const WISHLIST_KEY = 'carvalho_wishlist';

export const getWishlist = () => {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch {
    return [];
  }
};

export const toggleWishlistItem = (productId) => {
  const list = getWishlist();
  const idx = list.indexOf(productId);
  if (idx > -1) {
    list.splice(idx, 1);
  } else {
    list.push(productId);
  }
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  window.dispatchEvent(new Event('wishlist-updated'));
  return list;
};

export const isInWishlist = (productId) => {
  return getWishlist().includes(productId);
};

const Favoritos = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlistProducts = async () => {
    setLoading(true);
    const ids = getWishlist();
    if (ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/products', { params: { limit: 500 } });
      const filtered = (response.data || []).filter(p => ids.includes(p.id));
      setProducts(filtered);
    } catch (error) {
      console.error('Error fetching wishlist products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlistProducts();
    const handler = () => fetchWishlistProducts();
    window.addEventListener('wishlist-updated', handler);
    return () => window.removeEventListener('wishlist-updated', handler);
  }, []);

  const handleClearAll = () => {
    localStorage.setItem(WISHLIST_KEY, '[]');
    setProducts([]);
    window.dispatchEvent(new Event('wishlist-updated'));
  };

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50 pt-4 md:pt-8" data-testid="favoritos-page">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                Meus Favoritos
              </h1>
              <p className="text-slate-600">
                {products.length} {products.length === 1 ? 'item salvo' : 'itens salvos'}
              </p>
            </div>
            {products.length > 0 && (
              <Button variant="outline" onClick={handleClearAll} className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar Tudo
              </Button>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <div className="skeleton aspect-square w-full" />
                  <div className="p-3 space-y-2">
                    <div className="skeleton h-4 w-full" />
                    <div className="skeleton h-5 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
              <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                Nenhum favorito ainda
              </h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Clique no coracao nos produtos para salvar seus favoritos aqui.
              </p>
              <Button asChild className="bg-orange-600 hover:bg-orange-700">
                <Link to="/ofertas">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Explorar Ofertas
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Favoritos;
